"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ShieldAlert, Target, CalendarClock, ShieldCheck, Lightbulb, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Inspection",
  icon: ShieldAlert,
  description: "We begin with comprehensive ranch assessments and evaluation of agricultural drainage systems. Identifying moisture sources and hidden pest entry points is critical for securing rural properties against infestations.",
  insight: "Arcadia Insight: Focus on agricultural drainage."
}, {
  title: "Treatment",
  icon: Target,
  description: "Our treatments feature property-specific tailoring designed specifically for rural and older structures. We utilize advanced, targeted formulations that respect your land while aggressively eliminating active pest populations.",
  insight: "Arcadia Insight: Tailored for older structures."
}, {
  title: "Follow-up",
  icon: CalendarClock,
  description: "We establish a strict re-treatment scheduling protocol to ensure the initial knockdown is completely successful. Continuous evaluation during this phase prevents immediate re-infestation and breaks the breeding cycle.",
  insight: "Arcadia Insight: Strict re-treatment scheduling."
}, {
  title: "Prevention",
  icon: ShieldCheck,
  description: "Long-term success relies on year-round monitoring specifically adapted for rural properties. We continuously adjust our protective barriers to seasonal changes, keeping your property secure 365 days a year.",
  insight: "Arcadia Insight: Year-round seasonal monitoring."
}];
function TimelineItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-start md:items-center w-full transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="absolute left-4 md:left-1/2 w-12 h-12 bg-foreground border-4 border-background rounded-none -translate-x-1/2 flex items-center justify-center z-10 mt-4 md:mt-0 shadow-md">
        <Icon className="w-6 h-6 text-background" />
      </div>

      {}
      <div className={`w-full md:w-1/2 pl-16 md:pl-0 ${isEven ? "md:pr-16 text-left md:text-right" : "md:pl-16 text-left"}`}>
        <Card className="rounded-none border-4 border-foreground shadow-xl bg-card hover:bg-accent/5 transition-colors duration-300">
          <CardHeader className="pb-4">
            <div className={`flex items-center gap-4 ${isEven ? "md:flex-row-reverse" : ""}`}>
              <span className="text-5xl font-default font-bold text-muted/30 select-none">
                0{index + 1}
              </span>
              <CardTitle className="font-default text-3xl uppercase tracking-wide text-foreground">
                {step.title}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="font-serif text-lg leading-relaxed text-card-foreground/90">
              {step.description}
            </p>
            <div className={`flex ${isEven ? "md:justify-end" : "justify-start"}`}>
              <Badge variant="secondary" className="rounded-none font-default uppercase tracking-wider border-2 border-foreground bg-secondary text-secondary-foreground py-1.5 px-3 text-sm shadow-sm">
                <Lightbulb className="w-4 h-4 mr-2" />
                {step.insight}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function PestControlProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="pestcontrolprocess" className="py-32 bg-background text-foreground overflow-hidden relative">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
      backgroundSize: '32px 32px'
    }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"}`}>
          <Badge variant="outline" className="rounded-none border-foreground font-default uppercase tracking-widest mb-6 px-4 py-1 bg-background">
            Our Methodology
          </Badge>
          <h2 className="text-5xl md:text-7xl font-default font-bold uppercase tracking-tight mb-6 text-foreground drop-shadow-sm">
            Arcadia Professional Process
          </h2>
          <p className="font-serif text-xl md:text-2xl max-w-3xl mx-auto text-foreground/80 leading-relaxed">
            A rigorous, four-step industrial approach to pest eradication, designed specifically for the unique challenges of Florida's rural and residential properties.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="absolute left-4 md:left-1/2 top-8 bottom-8 w-1.5 bg-foreground -translate-x-1/2 shadow-sm"></div>

          <div className="space-y-16 md:space-y-24">
            {steps.map((step, i) => <TimelineItem key={i} step={step} index={i} />)}
          </div>
        </div>

        <div className="mt-24 text-center">
          <Button asChild size="lg" className="rounded-none font-default uppercase tracking-widest text-lg h-14 px-8 border-2 border-transparent hover:border-foreground transition-all shadow-lg hover:shadow-xl">
            <Link to="/ContactPage">
              Start The Process Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}